import * as React from "react";
import moment from "moment-timezone";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { deliveryDateHasPassed } from "src/core/orders/utils/orders";
import { useDrop } from "src/core/drops/hooks";
import { IOrder } from "src/core/orders/api/orders.api";

import useOrderIsBeforeCutoff from "./useOrderIsBeforeCutoff";

/**
 * Check if the order passes validation
 *
 * @param {IOrder} order - order object.
 * @param {IDrop} drop - drop object.
 *
 * @return {string | null} - The reason it didn't pass, or null if it does pass.
 */
const useCanSaveOrder = () => {
  const orderIsBeforeCutOff = useOrderIsBeforeCutoff();
  const [ { drop } ] = useDrop();

  const canSaveOrder = React.useCallback(
    (order: IOrder): string | null => {
      if (!drop) {
        return "Drop not found";
      }

      const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

      if (!leadTimes || !defaultCutoff) {
        return "Order cannot be submitted.";
      }

      // Only do delivery date validation if it's a date-led order
      if (order.orderDeliveryDate) {
        // if the delivery date is in the past then - FAIL
        if (deliveryDateHasPassed(order.orderDeliveryDate)) {
          return "Order cannot be submitted, as delivery date is in the past";
        }

        // if the deliverydate is before cutoff then - FAIL
        if (orderIsBeforeCutOff(order, "longest")) {
          if (order.amendmentExpiryDate && moment.utc().isAfter(order.amendmentExpiryDate) && moment.utc(order.amendmentExpiryDate).isAfter(moment.utc())) {
            return "Order cannot be amended, as the amendment expiry time has passed";
          }

          return "Order cannot be submitted, as cutoff has passed";
        }
      }

      return null;
    },
    [ drop ]
  );

  return canSaveOrder;
};

export default useCanSaveOrder;
